'use client'

import { useTranslations } from 'next-intl'
import { Tabs } from 'radix-ui'
import { useRef } from 'react'

import { CATEGORY_TABS } from '../data/category-tabs'
import { ServiceAccordion } from './accordion'

interface ICategoryTabsProps {
  selectedTab: string
  onSelectTab: (tabId: string) => void
}

export function CategoryTabs({ selectedTab, onSelectTab }: ICategoryTabsProps) {
  const t = useTranslations('Services')
  const tabsListRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  const scrollToTab = (tabId: string) => {
    const selectedTabElement = tabRefs.current[tabId]
    if (selectedTabElement && tabsListRef.current) {
      const tabsList = tabsListRef.current
      const tabElement = selectedTabElement

      // Calculate the position to scroll to center the tab
      const tabLeft = tabElement.offsetLeft
      const tabWidth = tabElement.offsetWidth
      const containerWidth = tabsList.offsetWidth
      const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2

      tabsList.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  const handleTabChange = (tabId: string) => {
    onSelectTab(tabId)
    scrollToTab(tabId)
  }

  return (
    <Tabs.Root
      className="flex w-full flex-col px-4"
      defaultValue="utilities"
      value={selectedTab}
      onValueChange={handleTabChange}
    >
      <Tabs.List
        ref={tabsListRef}
        className="no-scrollbar mx-auto flex w-full max-w-160 shrink-0 gap-4 overflow-x-scroll md:mb-6 md:justify-center md:gap-8"
        aria-label={t('categories.title')}
      >
        {CATEGORY_TABS.map((category) => (
          <Tabs.Trigger
            ref={(el) => {
              tabRefs.current[category.id] = el
            }}
            key={category.id}
            className="text-primary-400 hover:text-primary-500 data-[state=active]:text-primary-500 data-[state=active]:border-primary-500 text-body-3 flex h-11.25 shrink-0 cursor-pointer items-center justify-center bg-white px-2 ring-offset-1 transition-all ease-in data-[state=active]:border-b-2"
            value={category.id}
          >
            {t(category.title)}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {CATEGORY_TABS.map((category) => (
        <Tabs.Content
          key={category.id}
          value={category.id}
          className="mx-auto my-4 w-full max-w-240 space-y-4"
        >
          {category.types.map((type, index) => (
            <ServiceAccordion
              key={type.name}
              title={type.name}
              value={type.name}
              defaultOpen={index === 0}
              type={type}
            />
          ))}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  )
}
